import { useEffect, useRef, type PropsWithChildren, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useToast } from '../../../components/ui/use-toast.tsx';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import { useManagerViewEnabled, useViewMode } from '../hooks/use-view-mode.ts';
import { isManagerViewOnlyPath, isRepViewOnlyPath } from '../view-mode-paths.ts';

/**
 * Keeps a manager in the view that owns the page they navigate to.
 *
 * A manager can reach every page, but each view-specific page belongs to one
 * view (manager-only: /team, /activities; rep-only: /drafts,
 * /events). When a manager navigates — via a direct link, bookmark, shared URL,
 * restored tab — to a page that doesn't belong to their current view, we switch
 * them into the view that owns it (so the page renders in its proper chrome)
 * and toast that it's only viewable in that mode.
 *
 * Crucially this reacts to NAVIGATION, not to view-mode changes: a deliberate
 * toggle of the view switch is handled by the switch itself (it changes the
 * mode and navigates home). If the guard also reacted to the mode change it
 * would observe the new mode on the old page — via the cross-instance
 * `storage` sync in useLocalStorage — and bounce the user straight back. So we
 * only act when the pathname actually changes.
 *
 * Deliberately inert for actual reps and for managers without the manager
 * experience (`managerViewEnabled` is false for both): they're locked to Rep
 * and these pages are role-gated, so the situation can't arise.
 *
 * Must be mounted inside AclProvider (the view hooks read AppConfig).
 */
export const ViewModePageGuard = ({ children }: PropsWithChildren): ReactNode => {
    const { pathname } = useLocation();
    const { t } = useLocale('common');
    const { toast } = useToast();
    const managerViewEnabled = useManagerViewEnabled();
    const { viewMode, setViewMode } = useViewMode();

    // null until the first navigation is handled, so the initial render (a
    // direct link / fresh load) counts as a navigation and is reconciled.
    const lastHandledPathname = useRef<string | null>(null);

    useEffect(() => {
        const navigated = lastHandledPathname.current !== pathname;
        lastHandledPathname.current = pathname;

        if (!navigated || !managerViewEnabled) {
            return;
        }

        if (viewMode === 'MANAGER' && isRepViewOnlyPath(pathname)) {
            setViewMode('REP');
            toast({ icon: 'info', title: t('view_switch__rep_only_page_toast') });
        } else if (viewMode === 'REP' && isManagerViewOnlyPath(pathname)) {
            setViewMode('MANAGER');
            toast({ icon: 'info', title: t('view_switch__manager_only_page_toast') });
        }
    }, [pathname, managerViewEnabled, viewMode, setViewMode, toast, t]);

    return <>{children}</>;
};
